import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import Card from '../components/common/Card';
import PageHero from '../components/common/PageHero';
import Table from '../components/common/Table';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Select from '../components/common/Select';
import Pagination from '../components/common/Pagination';
import usePagination from '../hooks/usePagination';
import { SERVICE_TYPES, PAYMENT_TYPES } from '../utils/constants';
import { formatDate, formatCurrency, getServiceTypeLabel } from '../utils/helpers';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const Reports = () => {
  const { invoices, quotations, loadingInvoices } = useApp();
  
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    serviceType: '',
    paymentType: ''
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      dateFrom: '',
      dateTo: '',
      serviceType: '',
      paymentType: ''
    });
  };

  // Filter invoices based on criteria
  const filteredData = useMemo(() => {
    return invoices.filter(inv => {
      // Date filter
      if (filters.dateFrom) {
        const invoiceDate = inv.createdAt?.toDate ? inv.createdAt.toDate() : new Date(inv.createdAt);
        if (invoiceDate < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo) {
        const invoiceDate = inv.createdAt?.toDate ? inv.createdAt.toDate() : new Date(inv.createdAt);
        if (invoiceDate > new Date(filters.dateTo + 'T23:59:59')) return false;
      }
      
      // Service type filter
      if (filters.serviceType && inv.serviceType !== filters.serviceType) return false;
      
      // Payment type filter
      if (filters.paymentType && inv.paymentType !== filters.paymentType) return false;
      
      return true;
    });
  }, [invoices, filters]);

  // Calculate summary stats
  const summary = useMemo(() => {
    return {
      totalInvoices: filteredData.length,
      totalRevenue: filteredData.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0),
      avgInvoice: filteredData.length > 0 
        ? filteredData.reduce((sum, inv) => sum + (inv.totalAmount || 0), 0) / filteredData.length 
        : 0,
      uniqueCustomers: new Set(filteredData.map(inv => inv.phone)).size
    };
  }, [filteredData]);

  // Daily sales data
  const dailySalesData = useMemo(() => {
    const daily = {};
    filteredData.forEach(inv => {
      const date = formatDate(inv.createdAt, 'dd/MM');
      daily[date] = (daily[date] || 0) + (inv.totalAmount || 0);
    });
    return Object.entries(daily)
      .slice(-14)
      .map(([date, amount]) => ({ date, amount }));
  }, [filteredData]);

  // Service type breakdown
  const serviceBreakdown = useMemo(() => {
    const breakdown = {};
    filteredData.forEach(inv => {
      const type = getServiceTypeLabel(inv.serviceType);
      breakdown[type] = (breakdown[type] || 0) + (inv.totalAmount || 0);
    });
    return Object.entries(breakdown).map(([name, value]) => ({ name, value }));
  }, [filteredData]);

  const { page, setPage, pageSize, setPageSize, total, totalPages, pagedItems } = usePagination(
    filteredData,
    { persistKey: 'reports', resetKey: `${filters.dateFrom}|${filters.dateTo}|${filters.serviceType}|${filters.paymentType}` }
  );

  const columns = [
    {
      key: 'customerName',
      label: 'Macmiilka',
      render: (value, row) => (
        <div>
          <p className="font-medium">{value}</p>
          <p className="text-sm text-gray-500">{row.phone}</p>
        </div>
      )
    },
    {
      key: 'serviceType',
      label: 'Adeegga',
      render: (value) => getServiceTypeLabel(value)
    },
    {
      key: 'fromLocation',
      label: 'Ka Guray',
      render: (value) => <span className="text-sm">{value}</span>
    },
    {
      key: 'toLocation',
      label: 'Una Guuray',
      render: (value) => <span className="text-sm">{value}</span>
    },
    {
      key: 'createdAt',
      label: 'Taariikhda',
      render: (value) => formatDate(value)
    },
    {
      key: 'paymentType',
      label: 'Lacagta',
      render: (value) => (
        <span className="px-2 py-1 bg-secondary-50 text-secondary-700 rounded text-xs">
          {value}
        </span>
      )
    },
    {
      key: 'totalAmount',
      label: 'Wadarta',
      render: (value) => (
        <span className="font-bold text-green-600">{formatCurrency(value)}</span>
      )
    }
  ];

  // Export to CSV
  const exportCSV = () => {
    const headers = ['Macmiilka', 'Telefoon', 'Adeegga', 'Ka', 'Ilaa', 'Taariikhda', 'Lacagta', 'Wadarta'];
    const rows = filteredData.map(inv => [
      inv.customerName,
      inv.phone,
      getServiceTypeLabel(inv.serviceType),
      inv.fromLocation,
      inv.toLocation,
      formatDate(inv.createdAt),
      inv.paymentType,
      inv.totalAmount
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <div className="space-y-4">
      <PageHero
        accent="green"
        eyebrow="Xisaabaadka"
        title="Warbixinaha"
        subtitle="Warbixinta guud ee macaamiisha iyo dakhliga"
        actions={
          <Button onClick={exportCSV} variant="secondary" icon={<span>📥</span>}>
            Soo deji CSV
          </Button>
        }
        stats={[
          { label: 'Invoices', value: summary.totalInvoices },
          { label: 'Dakhliga', value: formatCurrency(summary.totalRevenue) },
          { label: 'Celcelis', value: formatCurrency(summary.avgInvoice) },
          { label: 'Macaamiil', value: summary.uniqueCustomers }
        ]}
      />

      {/* Filters */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Input
            label="Laga bilaabo"
            name="dateFrom"
            type="date"
            value={filters.dateFrom}
            onChange={handleFilterChange}
          />
          <Input
            label="Ilaa"
            name="dateTo"
            type="date"
            value={filters.dateTo}
            onChange={handleFilterChange}
          />
          <Select
            label="Nooca Adeegga"
            name="serviceType"
            value={filters.serviceType}
            onChange={handleFilterChange}
            options={SERVICE_TYPES}
            placeholder="Dhammaan"
          />
          <Select
            label="Nooca Lacagta"
            name="paymentType"
            value={filters.paymentType}
            onChange={handleFilterChange}
            options={PAYMENT_TYPES}
            placeholder="Dhammaan"
          />
          <div className="flex items-end">
            <Button variant="ghost" onClick={clearFilters} fullWidth>
              Tirtir Filters
            </Button>
          </div>
        </div>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Sales */}
        <Card title="Iibka Maalinlaha" subtitle="14 Maalmood ee ugu dambeyay">
          {dailySalesData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailySalesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Line 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#C62828" 
                    strokeWidth={2}
                    dot={{ fill: '#C62828' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Macluumaad lama helin
            </div>
          )}
        </Card>

        {/* Service Type Breakdown */}
        <Card title="Qaybinta Nooca Adeegga">
          {serviceBreakdown.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={serviceBreakdown} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={120} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Bar dataKey="value" fill="#0D47A1" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              Macluumaad lama helin
            </div>
          )}
        </Card>
      </div>

      <Card title="Liiska Macaamiisha" padding={false}>
        <Table
          columns={columns}
          data={pagedItems}
          loading={loadingInvoices}
          emptyMessage="Macluumaad lama helin"
        />
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </Card>
    </div>
  );
};

export default Reports;